// Hook para gerenciar operações com partes e riscos
import { useState, useCallback } from 'react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

interface UseApreciacaoPartesProps {
  apreciacaoId: string | undefined;
  riscos: any[];
  setRiscos: (riscos: any[]) => void;
}

export function useApreciacaoPartes({
  apreciacaoId,
  riscos,
  setRiscos,
}: UseApreciacaoPartesProps) {
  const { t } = useI18n();
  const [modalParteAberto, setModalParteAberto] = useState(false);
  const [parteSelecionada, setParteSelecionada] = useState<{ 
    parte: string; 
    riscos: any[]; 
    numero: number 
  } | null>(null);

  const handleEditarParte = useCallback((parte: string, riscosParte: any[], numero: number) => {
    setParteSelecionada({ parte, riscos: riscosParte, numero });
    setModalParteAberto(true);
  }, []);

  const handleFecharModal = useCallback(() => {
    setModalParteAberto(false);
    setParteSelecionada(null);
  }, []);

  const handleDeletarParte = useCallback(async (parte: string, riscosParte: any[]) => {
    try {
      const promises = riscosParte.map(async (risco) => {
        if (risco.id) {
          const response = await apiService.removerRisco(risco.id);
          if (!response.success) {
            throw new Error(
              t('apreciacoes.salvar.partes.erroRemoverRisco', {
                numero: risco.numero,
                detalhe: response.message || t('apreciacoes.erroDesconhecido'),
              })
            );
          }
        }
      });

      const results = await Promise.allSettled(promises);
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        const msgs = failures
          .map((r) => (r as PromiseRejectedResult).reason?.message || t('apreciacoes.erroDesconhecido'))
          .join('; ');
        alert(t('apreciacoes.salvar.partes.erroRemoverAlguns', { detalhe: msgs }));
      }
      // Remove riscos deletados com sucesso (por id) e riscos locais (sem id) da mesma parte
      const deletedIds = new Set(
        riscosParte
          .filter((_, i) => results[i].status === 'fulfilled')
          .map((r) => r.id)
          .filter(Boolean)
      );
      setRiscos(riscos.filter((r) => {
        if (r.parte !== parte) return true; // outra parte: manter
        if (!r.id) return false;            // risco local da parte: remover
        return !deletedIds.has(r.id);       // risco salvo: manter se falhou
      }));
    } catch (error: any) {
      console.error('Erro ao deletar parte:', error);
      alert(
        t('apreciacoes.salvar.partes.erroDeletar', {
          detalhe: error.message || t('apreciacoes.erroDesconhecido'),
        })
      );
    }
  }, [riscos, setRiscos, t]);

  const handleDuplicarParte = useCallback(async (parte: string, riscosParte: any[]) => {
    if (!apreciacaoId) {
      alert(t('apreciacoes.salvar.partes.semId'));
      return;
    }

    try {
      // Criar novo nome para a parte duplicada
      const partesExistentes = new Set(riscos.map(r => r.parte || ''));
      let novoNomeParte = `${parte} (Cópia)`;
      let contador = 1;

      while (partesExistentes.has(novoNomeParte)) {
        contador++;
        novoNomeParte = `${parte} (Cópia ${contador})`;
      }

      const maxNumero = riscos.length > 0
        ? Math.max(...riscos.map(r => r.numero || 0))
        : 0;

      let proximoNumero = maxNumero + 1;

      // Criar cópias dos riscos
      const riscosDuplicados = riscosParte.map((riscoOriginal) => {
        const { id, ...riscoSemId } = riscoOriginal;
        return {
          ...riscoSemId,
          numero: proximoNumero++,
          parte: novoNomeParte,
          tipoPerigo: riscoOriginal.tipoPerigo || 'mecanico',
          categoria: riscoOriginal.categoria || '',
          gravidadeAtual: riscoOriginal.gravidadeAtual ?? 1,
          frequenciaAtual: riscoOriginal.frequenciaAtual ?? 1,
          exposicaoAtual: riscoOriginal.exposicaoAtual ?? 1,
          probabilidadeAtual: riscoOriginal.probabilidadeAtual ?? 1,
          cicloVida: riscoOriginal.cicloVida || '',
          eventoPerigoso: riscoOriginal.eventoPerigoso || '',
          consequencias: riscoOriginal.consequencias || '',
          conformeAtual: riscoOriginal.conformeAtual ?? false,
        };
      });

      // Salvar riscos duplicados
      const riscosSalvos: any[] = [];
      for (const risco of riscosDuplicados) {
        const riscoParaSalvar: any = {
          numero: risco.numero || 1,
          parte: risco.parte || '',
          tipoPerigo: risco.tipoPerigo || 'mecanico',
          categoria: risco.categoria || '',
          gravidadeAtual: risco.gravidadeAtual ?? 1,
          frequenciaAtual: risco.frequenciaAtual ?? 1,
          exposicaoAtual: risco.exposicaoAtual ?? 1,
          probabilidadeAtual: risco.probabilidadeAtual ?? 1,
          cicloVida: risco.cicloVida || '',
          eventoPerigoso: risco.eventoPerigoso || '',
          consequencias: risco.consequencias || '',
          adequacoesTecnicas: risco.adequacoesTecnicas,
          medidasControle: risco.medidasControle,
          exigenciasNormativas: risco.exigenciasNormativas,
          adequacoesNormativas: risco.adequacoesNormativas,
          plr: risco.plr,
          sil: risco.sil,
          categoriaSeguranca: risco.categoriaSeguranca,
          classificacaoRisco: risco.classificacaoRisco,
          conformeAtual: risco.conformeAtual ?? false,
          fotoAtualUrl1: risco.fotoAtualUrl1,
          fotoAtualUrl2: risco.fotoAtualUrl2,
          fotoAtualUrl3: risco.fotoAtualUrl3,
          fotoAtualUrl4: risco.fotoAtualUrl4,
          fotoPlrUrl: risco.fotoPlrUrl,
          ordem: risco.ordem || 0,
        };

        // Remover campos undefined/null/vazios
        Object.keys(riscoParaSalvar).forEach(key => {
          const value = riscoParaSalvar[key];
          if (value === undefined || value === null || 
              (typeof value === 'string' && value.trim() === '' && 
               key !== 'parte' && key !== 'cicloVida' && key !== 'eventoPerigoso' && key !== 'consequencias')) {
            delete riscoParaSalvar[key];
          }
        });

        const riscoResponse = await apiService.adicionarRisco(apreciacaoId, riscoParaSalvar);
        if (riscoResponse.success && riscoResponse.data) {
          riscosSalvos.push(riscoResponse.data);
        } else {
          throw new Error(
            t('apreciacoes.salvar.partes.erroSalvarRisco', {
              numero: risco.numero,
              detalhe: riscoResponse.message || t('apreciacoes.erroDesconhecido'),
            })
          );
        }
      }

      setRiscos([...riscos, ...riscosSalvos]);
      alert(
        t('apreciacoes.salvar.partes.duplicada', {
          parte: novoNomeParte,
          total: riscosSalvos.length,
        })
      );
    } catch (error: any) {
      console.error('Erro ao duplicar parte:', error);
      alert(
        t('apreciacoes.salvar.partes.erroDuplicar', {
          detalhe: error.message || t('apreciacoes.erroDesconhecido'),
        })
      );
    }
  }, [apreciacaoId, riscos, setRiscos, t]);

  const handleAdicionarParte = useCallback(() => {
    // Calcular o próximo número da parte
    const partesUnicas = new Set(riscos.map(r => r.parte || ''));
    const proximoNumero = partesUnicas.size + 1;
    
    // Criar nome padrão para a nova parte
    let nomeParte = `Parte ${proximoNumero}`;
    let contador = 1;
    while (partesUnicas.has(nomeParte)) {
      contador++;
      nomeParte = `Parte ${proximoNumero} (${contador})`;
    }

    // Abrir modal com parte nova (sem riscos)
    setParteSelecionada({ 
      parte: nomeParte, 
      riscos: [], 
      numero: proximoNumero 
    });
    setModalParteAberto(true);
  }, [riscos]);

  const calcularNovaOrdem = (novaOrdemPartes: string[]): Array<{ risco: any; numero: number }> => {
    const riscosOrdenados = [...riscos].sort((a, b) => (a.numero || 0) - (b.numero || 0));
    const riscosPorParte = new Map<string, any[]>();
    novaOrdemPartes.forEach((nomeParte) => {
      riscosPorParte.set(
        nomeParte,
        riscosOrdenados
          .filter((r) => (r.parte || 'Sem parte definida') === nomeParte)
          .sort((a, b) => (a.numero || 0) - (b.numero || 0))
      );
    });
    const atualizacoes: Array<{ risco: any; numero: number }> = [];
    let contador = 1;
    novaOrdemPartes.forEach((nomeParte) => {
      (riscosPorParte.get(nomeParte) || []).forEach((risco) => {
        atualizacoes.push({ risco, numero: contador++ });
      });
    });
    return atualizacoes;
  };

  const aplicarOrdemLocalmente = (atualizacoes: Array<{ risco: any; numero: number }>) => {
    const mapa = new Map<string, number>();
    atualizacoes.forEach((item) => {
      if (item.risco.id) mapa.set(item.risco.id, item.numero);
    });
    return riscos.map((r) => {
      if (r.id && mapa.has(r.id)) return { ...r, numero: mapa.get(r.id) };
      const found = atualizacoes.find((a) => !a.risco.id && a.risco === r);
      return found ? { ...r, numero: found.numero } : r;
    });
  };

  const handleReordenarPartes = useCallback(async (novaOrdemPartes: string[]) => {
    if (!apreciacaoId) return;

    const atualizacoes = calcularNovaOrdem(novaOrdemPartes);

    // Atualização otimista: atualizar UI imediatamente
    const riscosAnteriores = riscos;
    setRiscos(aplicarOrdemLocalmente(atualizacoes));

    // Sincronizar com backend em background
    const ordemPayload = atualizacoes
      .filter((item) => item.risco.id)
      .map((item) => ({ id: item.risco.id, numero: item.numero }));

    try {
      const resp = await apiService.reordenarRiscos(apreciacaoId, ordemPayload);
      if (!resp.success) {
        // Reverter em caso de erro
        setRiscos(riscosAnteriores);
        alert(
          t('apreciacoes.salvar.partes.erroReordenar', {
            detalhe: resp.message || t('apreciacoes.erroDesconhecido'),
          })
        );
      }
    } catch (error: any) {
      setRiscos(riscosAnteriores);
      console.error('Erro ao reordenar partes:', error);
      alert(
        t('apreciacoes.salvar.partes.erroReordenar', {
          detalhe: error?.message || t('apreciacoes.erroDesconhecido'),
        })
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apreciacaoId, riscos, setRiscos, t]);

  return {
    modalParteAberto,
    parteSelecionada,
    handleEditarParte,
    handleFecharModal,
    handleDeletarParte,
    handleDuplicarParte,
    handleAdicionarParte,
    handleReordenarPartes,
  };
}
